// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'

export type Layer = 'accessory' | 'main' | 'navigation' | 'panel' | 'popup'
export type Level = 'root' | 'section' | 'subsection'
export type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
export type TitleVariant =
  | 'web-main-section-display'
  | 'web-main-section-hero'
  | 'web-main-subsection-card'
  | 'web-promo'
export type Alignment = 'center' | 'end' | 'start'

/**
 * @slot Content to display for the title.
 * @see https://quartz.se.com/build/components/title
 */
@Component({
  tag: 'qds-title',
  shadow: true,
  styleUrl: 'title.css',
})
export class Title implements ComponentInterface {
  /**
   * The alignment of the title.
   */
  @Prop() public readonly alignment?: Alignment = 'start'

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * Text to display for the kicker.
   */
  @Prop() public readonly kicker?: string

  @Prop() public readonly layer?: Layer = 'main'

  @Prop() public readonly level?: Level = 'section'

  /**
   * Text to display for the subtitle.
   */
  @Prop() public readonly subtitle?: string

  /**
   * HTML tag to use to render the title.
   */
  @Prop() public readonly tag?: Tag = 'h2'

  /**
   * The title's variant.
   */
  @Prop() public readonly variant?: TitleVariant

  @Element() private readonly host!: HTMLElement

  #inheritedAttributes: Attributes = {}

  get #hasSubtitle(): boolean {
    return this.subtitle !== undefined && this.subtitle !== ''
  }

  get #hasKicker(): boolean {
    return this.kicker !== undefined && this.kicker !== ''
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    const layer = `qds-${this.layer ?? 'main'}`
    const level = `qds-${this.level ?? 'section'}`
    const Tag = this.tag ?? 'h2'
    const hasIcon =
      this.iconName !== undefined &&
      this.iconName !== '' &&
      this.variant !== 'web-promo'

    return (
      <hgroup
        data-alignment={this.alignment ?? 'start'}
        data-variant={this.variant}
        class={{
          [layer]: true,
          [level]: true,
          'qds-titles': true,
          'qds-has-icon': hasIcon,
          'qds-has-kicker': this.#hasKicker,
          'qds-has-subtitle': this.#hasSubtitle,
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      >
        <Tag class="qds-title" data-variant={this.variant}>
          <slot />
        </Tag>
        {this.#hasKicker && <p class="qds-kicker">{this.kicker}</p>}
        {this.#hasSubtitle && <p class="qds-subtitle">{this.subtitle}</p>}
        {hasIcon && (
          <qds-icon
            aria-hidden="true"
            class="qds-icon"
            name={this.iconName}
            library={this.iconLibrary}
          />
        )}
      </hgroup>
    )
  }
}
